/* ═══════════════════════════════════════════════════════════════════════════
   O texto da página, num arquivo só.

   Quem mexe aqui está mudando o que a página DIZ; quem mexe nos organisms está
   mudando como ela se PARECE. Foi por isso que o conteúdo saiu de dentro do
   JSX: trocar a chamada de uma seção não deveria exigir abrir um componente e
   passar por cima de classes de layout.

   Regra do que entra: só afirmação que o produto sustenta hoje. Cada item desta
   página existe nos repositórios `mypedidos-admin-frontend` e
   `mypedidos-cardapio-frontend`: kanban de três etapas, taxa por km, meio a
   meio, Pix com QR. O que ainda não existe aparece marcado como "em breve",
   como o Dashboard, que no admin está no menu e desabilitado.

   Regra de estilo: frases curtas, sem travessão. Onde o rascunho anterior usava
   "—" para encaixar uma segunda ideia na mesma frase, aqui ela vira frase
   própria, com ponto.

   ⚠ A CONFERIR antes de publicar: os campos marcados com REVISAR são o que a
   página não consegue extrair do código, hoje só o contato. O preço do plano
   já veio confirmado (R$ 149,99/mês, plano único, sem taxa por pedido).
   ═══════════════════════════════════════════════════════════════════════════ */

/* REVISAR: número, e-mail, domínio de produção e slug do cardápio de
   demonstração. O `whatsapp` vai cru para o `wa.me`, então é DDI + DDD +
   número, só dígitos. */
export const contato = {
  whatsapp: '555391073318',
  email: 'contato@mypedidos.com.br',
  appUrl: 'https://admin.mypedidos.com.br',
  menuUrl: 'https://cardapio.mypedidos.com.br',
  demoUrl: 'https://cardapio.mypedidos.com.br/hogwarts',
};

/** A mensagem que já vem escrita quando o lojista toca em "Falar no WhatsApp".
 *  Mesma técnica do cardápio: sem emoji, que não sobrevive ao `wa.me?text=` e
 *  chega como losango preto. */
const MENSAGEM_CONTATO =
  'Olá! Vi a página do MyPedidos e quero conhecer o sistema para o meu estabelecimento.';

export const linkWhatsapp = `https://wa.me/${contato.whatsapp}?text=${encodeURIComponent(MENSAGEM_CONTATO)}`;

export const cta = {
  principal: { label: 'Quero testar no meu restaurante', href: linkWhatsapp },
  secundaria: { label: 'Ver como funciona', href: '#como-funciona' },
};

export const navegacao = [
  { label: 'Como funciona', href: '#como-funciona' },
  { label: 'Para o cliente', href: '#cardapio' },
  { label: 'Para você', href: '#painel' },
  { label: 'Entrega e pagamento', href: '#operacao' },
  { label: 'Planos', href: '#planos' },
  { label: 'Perguntas', href: '#perguntas' },
];

/** O título do herói entra em três partes, e não como frase única, porque duas
 *  palavras têm tratamento visual diferente: o nome da marca (cor sólida) e a
 *  promessa central (sublinhado). `Hero.js` monta as três; nenhum outro lugar
 *  da página precisa desse formato, por isso ele não virou um tipo genérico. */
export const heroi = {
  selo: 'Em operação com os primeiros estabelecimentos',
  titulo: {
    antes: '',
    marca: 'MyPedidos',
    meio: ': seu cardápio online e o ',
    destaque: 'pedido pronto no WhatsApp',
  },
  texto:
    'O cliente monta o pedido pelo link e ele chega pronto no seu WhatsApp, com itens, endereço e total organizados.',
  notas: [
    'Sem aplicativo para o cliente baixar',
    'Painel próprio, do pagamento ao pedido pronto',
    'No ar no mesmo dia',
  ],
};

/* Atributos do produto, não métricas de negócio: número de clientes e volume de
   pedidos mudam toda semana e ninguém aqui teria como manter. Estes quatro são
   verificáveis no código. */
export const numeros = [
  { valor: '1 link', rotulo: 'cardapio.mypedidos.com.br/seu-restaurante' },
  { valor: '3 etapas', rotulo: 'do pagamento ao pedido pronto' },
  { valor: '15s', rotulo: 'o painel se atualiza sozinho' },
  { valor: '0', rotulo: 'aplicativos para instalar' },
];

export const passos = [
  {
    titulo: 'Monte o cardápio',
    texto: 'Categorias, produtos, fotos e adicionais. Arraste para ordenar e desligue o que acabou.',
  },
  {
    titulo: 'Compartilhe o link',
    texto: 'Você recebe um endereço com o nome do seu restaurante, pronto para o Instagram e o status.',
  },
  {
    titulo: 'O pedido cai no seu painel, já organizado',
    texto: 'Itens, variações, adicionais e pagamento chegam prontos no seu painel, sem nada para você digitar.',
  },
  {
    titulo: 'Acompanhe até o fim',
    texto: 'Arraste o pedido pelas etapas do painel. O cliente vê o mesmo status na tela dele.',
  },
];

export const cardapio = {
  kicker: 'Para o seu cliente',
  titulo: 'Um cardápio que ele entende no primeiro toque',
  texto: 'Abre pelo link, sem cadastro para olhar e sem app para baixar.',
  itens: [
    {
      icone: 'IconSmartphone',
      titulo: 'Feito para o celular',
      texto: 'Busca, categorias fixas no topo e carrinho sempre à mão. No computador, vira uma coluna ao lado.',
    },
    {
      icone: 'IconLayers',
      titulo: 'Tamanhos, meio a meio e adicionais',
      texto: 'Pizza de dois sabores paga o preço da metade mais cara, nunca a soma.',
    },
    {
      icone: 'IconPalette',
      titulo: 'Com a cara do seu negócio',
      texto: 'Seu logo no topo e sua cor em botões e destaques. O cliente vê a sua marca, não a nossa.',
    },
    {
      icone: 'IconMapPin',
      titulo: 'Endereço salvo e taxa na tela',
      texto: 'Busca pelo CEP, guarda o endereço na conta e mostra a taxa antes de fechar o pedido.',
    },
    {
      icone: 'IconClock',
      titulo: 'Acompanhamento do pedido',
      texto: '"Confirmando", "em preparo", "pronto para retirada". A tela se atualiza sozinha.',
    },
    {
      icone: 'IconShield',
      titulo: 'Legível para todo mundo',
      texto: 'Contraste conferido, alvos de toque de 44px e navegação por teclado.',
    },
  ],
};

export const painel = {
  kicker: 'Para você',
  titulo: 'O quadro de pedidos que a cozinha consegue seguir',
  texto: 'Três colunas, um pedido por cartão. Arrastar muda a etapa na hora, sem esperar o servidor.',
  itens: [
    'Kanban de **confirmação de pagamento**, **preparando** e **pronto**, com contador por coluna.',
    'No celular, as colunas viram abas e cada cartão ganha um botão **Avançar**.',
    'A lista **se atualiza sozinha a cada 15 segundos** e ao voltar para a aba.',
    'Busca por **nome do cliente ou número do pedido**, com detalhe completo em um toque.',
    '**PDV para o balcão**: monta o pedido de quem chegou na loja e ele entra no mesmo quadro.',
    'Cardápio com **foto, variações e adicionais**, além de ligar e desligar itens que acabaram.',
  ],
  rodape: 'Dashboard com faturamento e produtos mais vendidos: em breve.',
};

export const operacao = {
  kicker: 'Entrega e pagamento',
  titulo: 'As suas regras, aplicadas antes de o pedido existir',
  texto: 'Configure uma vez e o cardápio cobra sozinho, sem combinar taxa por mensagem.',
  itens: [
    {
      icone: 'IconMoped',
      variante: 'purple',
      titulo: 'Taxa por distância',
      texto: 'Taxa base mais um valor por quilômetro, medido entre a sua loja e o cliente.',
    },
    {
      icone: 'IconStore',
      variante: 'purple',
      titulo: 'Três formas de atender',
      texto: 'Entrega, retirada no balcão e consumo no local. O cliente só vê as que você deixou ligadas.',
    },
    {
      icone: 'IconQrCode',
      variante: 'amber',
      titulo: 'Pix com QR Code',
      texto: 'Chave, beneficiário e copia e cola gerados na tela do cliente.',
    },
    {
      icone: 'IconDollarSign',
      variante: 'amber',
      titulo: 'Dinheiro e maquininha',
      texto: 'Crédito e débito só entram se você marcar. O padrão não inclui cartão.',
    },
    {
      icone: 'IconClock',
      variante: 'purple',
      titulo: 'Horário de funcionamento',
      texto: 'Um intervalo por dia da semana. Fora dele o cardápio fica no ar só para consulta.',
    },
    {
      icone: 'IconDollarSign',
      variante: 'purple',
      titulo: 'Pedido mínimo',
      texto: 'Conferido sobre os itens, sem a taxa de entrega.',
    },
  ],
};

/** A mensagem que o cliente envia, como ela chega de verdade na conversa.
 *  Reproduzida do `utils/whatsapp.js` do cardápio, com os mesmos símbolos: sem
 *  emoji, régua com `━` e marcador `▸`. Reescrevê-la "mais bonita" aqui
 *  prometeria uma coisa e entregaria outra. */
export const mensagemExemplo = `*NOVO PEDIDO*
Pedido *#128* · Pizzaria do Zé
━━━━━━━━━━━━━━━

*ITENS*
▸ *1x* Pizza Grande · Calabresa / Marguerita · R$ 62,00
     + Borda de catupiry
▸ *2x* Coca-Cola lata · R$ 14,00
     R$ 7,00 cada

Subtotal dos itens: R$ 76,00

*ENTREGA*
▸ Marina Souza
▸ (51) 99123-4567
▸ Rua Padre Chagas, 240 · Moinhos de Vento, Porto Alegre/RS
▸ Apto 502
▸ Taxa de entrega: R$ 8,50
━━━━━━━━━━━━━━━
*TOTAL: R$ 84,50*

Segue o comprovante do pagamento.`;

/** A tela de acompanhamento que o link do pedido abre (`OrderStatusPage.js`).
 *  O texto da etapa é copiado do `switch` de `etapa()` daquele arquivo: são as
 *  mesmas palavras que o cliente lê de verdade, não uma paráfrase daqui. */
export const acompanhamento = {
  pedido: '#128',
  estabelecimento: 'Pizzaria do Zé',
  modalidade: 'Entrega',
  etapas: ['Confirmando pedido', 'Em preparo', 'Saiu para entrega'],
  etapaAtiva: 0,
  etapaTitulo: 'Confirmando seu pedido',
  etapaTexto: 'O estabelecimento está conferindo o pedido e o pagamento.',
  botaoWhatsapp: 'Enviar comprovante pelo WhatsApp',
};

export const whatsappSecao = {
  kicker: 'O pedido do jeito que chega',
  titulo: 'Nenhum "quanto ficou mesmo?" no meio do serviço',
  texto: 'A mensagem usa os valores que o sistema calculou, nunca os que a tela do cliente mostrava.',
  notas: [
    'O número do pedido é a primeira coisa que aparece',
    'Adicionais e observações ficam recuados sob o item',
    'A chave Pix vai sozinha na linha, para o toque longo copiar só ela',
    'O cliente também acompanha o pedido por uma página própria, na cor do seu estabelecimento',
  ],
};

/** Um plano só, com tudo incluído — sem degrau "básico" que esconde metade do
 *  produto atrás de um upsell. O valor veio do próprio combinado com você. */
export const plano = {
  preco: '149,99',
  unidade: '/mês',
  notaPreco: 'Sem taxa por pedido.',
  pitch: 'Tudo o que o seu restaurante precisa para vender pelo link, num só plano.',
  itens: [
    'Cardápio digital com link próprio',
    'Pedidos pelo WhatsApp, já formatados',
    'Quadro de pedidos com as três etapas',
    'PDV para atender quem chega na loja',
    'Taxa de entrega por distância',
    'Pix com QR Code e copia e cola',
    'Cor e logo da sua marca no cardápio',
    'Contas de cliente com endereço salvo',
  ],
  cta: 'Quero começar',
};

/** As quatro dúvidas que travam a decisão antes de qualquer outra, por isso
 *  ganham seção própria em vez de ficar diluídas no meio das sete perguntas
 *  do FAQ. Cada resposta tem no máximo duas frases e só afirma o que o
 *  produto sustenta hoje — nada de prazo de fidelidade ou política de dados
 *  que a página não tem como garantir. */
export const objecoes = [
  {
    pergunta: 'Tem taxa por pedido?',
    resposta: 'Não. Você paga só a mensalidade do plano, venda o que vender.',
  },
  {
    pergunta: 'Quanto tempo leva para configurar?',
    resposta: 'Dá para começar a receber pedido no mesmo dia. Cadastre primeiro os itens mais vendidos e complete o resto depois.',
  },
  {
    pergunta: 'E se eu quiser cancelar?',
    resposta: 'Cancela quando quiser, sem multa. Não existe fidelidade no plano.',
  },
  {
    pergunta: 'Preciso comprar equipamento novo?',
    resposta: 'Não. O painel abre no computador, tablet ou celular que você já usa, direto pelo navegador.',
  },
];

export const perguntas = [
  {
    p: 'Meu cliente precisa baixar algum aplicativo?',
    r: 'Não. O cardápio é uma página que abre no navegador do celular pelo link que você mandar. Ele pode salvar na tela inicial e abrir como um app, mas isso é escolha dele.',
  },
  {
    p: 'O pedido chega mesmo no WhatsApp?',
    r: 'Chega. Ao fechar o pedido, o cliente vai para a conversa do seu WhatsApp com a mensagem já escrita: itens, endereço, taxa e total. Ele só toca em enviar, junto com o comprovante do Pix.',
  },
  {
    p: 'Como funciona a taxa de entrega?',
    r: 'Você define uma taxa base e, se quiser, um valor por quilômetro. O sistema mede a distância entre a loja e o cliente e soma as duas partes.',
  },
  {
    p: 'Preciso de maquininha ou de conta em algum banco específico?',
    r: 'Não. O Pix usa a chave que você já tem, e o dinheiro cai direto na sua conta. Crédito e débito só aparecem para o cliente se você marcar que aceita.',
  },
  {
    p: 'Vendo pizza meio a meio. Dá para fazer?',
    r: 'Dá. O cliente escolhe dois sabores da mesma categoria e o preço é o da metade mais cara, nunca a soma. Dá para marcar tamanhos que não aceitam meio a meio.',
  },
  {
    p: 'E quem chega na loja, sem pedir pelo link?',
    r: 'Esse pedido você monta no PDV, dentro do painel, e ele entra no mesmo quadro dos pedidos do cardápio.',
  },
  {
    p: 'Quanto tempo leva para colocar no ar?',
    r: 'Dá para começar a receber pedido no mesmo dia. O cardápio pode ser cadastrado aos poucos, começando pelos itens mais vendidos.',
  },
];

export const chamadaFinal = {
  titulo: 'Pronto para receber o próximo pedido organizado?',
  texto: 'Fale com a gente pelo WhatsApp e já saia com o link do seu cardápio.',
};
